import { Link } from 'react-router-dom';
import SEOHead from '../../components/SEOHead';
import { Button } from '../../components/ui/button';
import { ArrowRight, Calculator, FileText, BarChart3, Users, Car, ShieldCheck, ScanLine, Layers, Receipt } from 'lucide-react';

const V = { brand: '#6366F1' };

export default function PageLogicielMarchand() {
    return (
        <div className="min-h-screen bg-white text-gray-900">
            <SEOHead
                title="Logiciel marchand automobile & gestion VO | Marge Auto"
                description="Marge Auto est le logiciel de gestion pour marchands automobiles. Suivi des marges, frais véhicules, commissions, TVA sur marge, multi-agences. Pilotez votre rentabilité VO."
                path="/logiciel-marchand-automobile"
                schema={{
                    "@context": "https://schema.org",
                    "@type": "SoftwareApplication",
                    "name": "Marge Auto",
                    "applicationCategory": "BusinessApplication",
                    "operatingSystem": "Web",
                    "description": "Logiciel de gestion financière pour marchands automobiles et professionnels du véhicule d'occasion.",
                    "url": "https://marge-auto.com/logiciel-marchand-automobile",
                    "offers": { "@type": "Offer", "price": "0", "priceCurrency": "EUR", "description": "Essai gratuit 30 jours" }
                }}
            />
            {/* Nav */}
            <nav className="border-b border-gray-100 bg-white/80 backdrop-blur-sm sticky top-0 z-50">
                <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
                    <Link to="/landing" className="text-xl font-bold tracking-tight" style={{ color: V.brand }}>Marge Auto</Link>
                    <div className="flex items-center gap-4">
                        <Link to="/auth" className="text-sm text-gray-500 hover:text-gray-900 hidden sm:inline">Se connecter</Link>
                        <Link to="/landing"><Button className="bg-[#6366F1] hover:bg-[#4F46E5] text-white text-sm h-9 px-4 rounded-lg">Demander un accès</Button></Link>
                    </div>
                </div>
            </nav>

            {/* Hero */}
            <header className="bg-gradient-to-b from-gray-950 to-gray-900 text-white py-20 sm:py-28">
                <div className="max-w-4xl mx-auto px-6 text-center">
                    <p className="text-sm font-medium text-indigo-400 uppercase tracking-wider mb-4">Logiciel professionnel automobile</p>
                    <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-tight mb-6">Le logiciel de gestion pour marchands automobiles</h1>
                    <p className="text-lg text-gray-400 max-w-2xl mx-auto mb-8">Centralisez le pilotage financier de votre activité automobile : marges, frais, commissions, TVA, rentabilité. Tout-en-un, véhicule par véhicule.</p>
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                        <Link to="/landing"><Button className="bg-[#6366F1] hover:bg-[#4F46E5] text-white h-12 px-8 text-base rounded-xl">Découvrir Marge Auto<ArrowRight className="ml-2 w-4 h-4" /></Button></Link>
                        <Link to="/calcul-marge-vehicule-occasion" className="text-sm text-gray-400 hover:text-white transition-colors underline underline-offset-4">En savoir plus sur le calcul de marge</Link>
                    </div>
                </div>
            </header>

            {/* Section: Problème métier */}
            <section className="py-16 sm:py-20 bg-white">
                <div className="max-w-5xl mx-auto px-6">
                    <h2 className="text-2xl sm:text-3xl font-bold text-center mb-4">Pourquoi un logiciel dédié aux marchands automobiles ?</h2>
                    <p className="text-gray-500 text-center max-w-3xl mx-auto mb-12">Un tableur ne suffit pas pour suivre la rentabilité réelle de chaque véhicule. Entre les frais dispersés, les commissions, la TVA et les charges, la marge affichée est rarement la marge réelle.</p>
                    <div className="grid md:grid-cols-3 gap-8">
                        {[
                            { icon: Car, title: 'Achat / Revente VO', text: "Suivez chaque véhicule de l'achat à la vente. Prix, frais, marge nette HT : tout est centralisé sur un seul dossier." },
                            { icon: Calculator, title: 'Marge réelle', text: "La marge brute ne suffit pas. Marge Auto déduit automatiquement les frais, la TVA sur marge et les charges pour afficher la rentabilité réelle." },
                            { icon: BarChart3, title: 'Pilotage agence', text: "Tableaux de bord, suivi des performances par commercial, objectifs mensuels, compte d'exploitation. Décidez avec des données fiables." },
                        ].map(f => (
                            <div key={f.title} className="rounded-xl border border-gray-200 p-6">
                                <f.icon className="w-8 h-8 text-[#6366F1] mb-4" />
                                <h3 className="text-lg font-semibold mb-2">{f.title}</h3>
                                <p className="text-sm text-gray-500 leading-relaxed">{f.text}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Section: Fonctionnalités */}
            <section className="py-16 sm:py-20 bg-gray-50">
                <div className="max-w-5xl mx-auto px-6">
                    <h2 className="text-2xl sm:text-3xl font-bold text-center mb-12">Les fonctionnalités de Marge Auto</h2>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {[
                            { icon: Calculator, title: 'Calcul de marge', text: 'Marge nette HT par véhicule avec TVA sur marge, frais déduits et remise client.' },
                            { icon: Receipt, title: 'Commissions', text: 'Commissions commerciales avec seuils, taux 40/50 % et marge minimale agence.' },
                            { icon: FileText, title: 'Frais véhicules', text: 'Rattachement par immatriculation, anti-doublon, remplacement de frais provisionnels.' },
                            { icon: Layers, title: 'Frais mutualisés', text: 'Répartition automatique des dépenses partagées sur les véhicules vendus du mois.' },
                            { icon: ScanLine, title: 'Scan intelligent', text: 'Scannez vos factures : extraction automatique, détection de plaque, rattachement proposé.' },
                            { icon: ShieldCheck, title: 'SAV & Retenues', text: "Gestion des dossiers SAV avec retenues sur commissions intégrées au calcul." },
                            { icon: BarChart3, title: "Compte d'exploitation", text: "CA, marges, commissions, charges : vision complète de la rentabilité de l'agence." },
                            { icon: Users, title: 'Multi-agences', text: 'Isolation par agence, rôles admin/commercial, accès différenciés.' },
                            { icon: FileText, title: 'Exports', text: "Export Excel et PDF des commissions, du compte d'exploitation et des frais." },
                        ].map(f => (
                            <div key={f.title} className="bg-white rounded-xl border border-gray-200 p-5">
                                <f.icon className="w-6 h-6 text-[#6366F1] mb-3" />
                                <h3 className="font-semibold mb-1">{f.title}</h3>
                                <p className="text-sm text-gray-500">{f.text}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Section: Pour qui */}
            <section className="py-16 sm:py-20 bg-white">
                <div className="max-w-4xl mx-auto px-6">
                    <h2 className="text-2xl sm:text-3xl font-bold text-center mb-10">Pour quels professionnels ?</h2>
                    <div className="grid sm:grid-cols-2 gap-6">
                        {[
                            { title: 'Marchands automobiles', text: "Vous achetez et revendez des véhicules d'occasion. Marge Auto suit la rentabilité de chaque opération." },
                            { title: 'Agences automobiles', text: "Vous gérez une équipe commerciale. Marge Auto calcule les commissions et suit les objectifs." },
                            { title: 'Garages avec achat-revente', text: "En plus de l'atelier, vous faites du négoce VO. Marge Auto isole la partie achat-revente." },
                            { title: 'Professionnels VO indépendants', text: "Même seul, suivre sa marge réelle évite les mauvaises surprises en fin de mois." },
                        ].map(p => (
                            <div key={p.title} className="border-l-4 border-[#6366F1] pl-5 py-2">
                                <h3 className="font-semibold mb-1">{p.title}</h3>
                                <p className="text-sm text-gray-500">{p.text}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section className="py-16 sm:py-20 bg-gray-50">
                <div className="max-w-3xl mx-auto px-6">
                    <h2 className="text-2xl sm:text-3xl font-bold text-center mb-10">Questions fréquentes</h2>
                    {[
                        { q: "Marge Auto est-il adapté aux petites structures ?", a: "Oui. Marge Auto fonctionne aussi bien pour un marchand indépendant que pour une agence multi-commerciaux. L'interface s'adapte au volume d'activité." },
                        { q: "Peut-on gérer plusieurs agences ?", a: "Oui. Chaque agence dispose de ses propres données, utilisateurs, règles de commission et paramètres. L'isolation est stricte entre agences." },
                        { q: "Comment sont calculées les commissions commerciales ?", a: "Marge Auto applique des taux configurables (par défaut 40 % sous le seuil, 50 % au-dessus) sur la marge nette, après déduction des frais, SAV et charges. Le calcul est automatique." },
                        { q: "Faut-il installer un logiciel ?", a: "Non. Marge Auto est un logiciel en ligne (SaaS) accessible depuis un navigateur, sur ordinateur, tablette ou smartphone." },
                        { q: "Les données sont-elles sécurisées ?", a: "Les données de chaque agence sont isolées. L'accès est protégé par authentification. Aucun utilisateur ne peut accéder aux données d'une autre agence." },
                    ].map((faq, i) => (
                        <details key={i} className="mb-3 bg-white rounded-xl border border-gray-200 overflow-hidden">
                            <summary className="px-5 py-4 cursor-pointer font-medium text-gray-900 hover:bg-gray-50">{faq.q}</summary>
                            <p className="px-5 pb-4 text-sm text-gray-500 leading-relaxed">{faq.a}</p>
                        </details>
                    ))}
                </div>
            </section>

            {/* CTA */}
            <section className="py-16 bg-gray-950 text-white text-center">
                <div className="max-w-2xl mx-auto px-6">
                    <h2 className="text-2xl sm:text-3xl font-bold mb-4">Prêt à piloter votre activité ?</h2>
                    <p className="text-gray-400 mb-8">Essayez Marge Auto gratuitement pendant 30 jours.</p>
                    <Link to="/landing"><Button className="bg-[#6366F1] hover:bg-[#4F46E5] text-white h-12 px-8 text-base rounded-xl">Demander un accès<ArrowRight className="ml-2 w-4 h-4" /></Button></Link>
                </div>
            </section>

            {/* Footer with internal links */}
            <footer className="py-10 bg-white border-t border-gray-100">
                <div className="max-w-5xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-gray-400">
                    <span>&copy; {new Date().getFullYear()} Marge Auto</span>
                    <div className="flex flex-wrap items-center gap-4">
                        <Link to="/logiciel-marchand-automobile" className="hover:text-gray-700">Logiciel marchand auto</Link>
                        <Link to="/calcul-marge-vehicule-occasion" className="hover:text-gray-700">Calcul de marge VO</Link>
                        <Link to="/gestion-frais-vehicules" className="hover:text-gray-700">Gestion des frais</Link>
                        <Link to="/suivi-marge-par-vehicule" className="hover:text-gray-700">Suivi marge véhicule</Link>
                        <Link to="/landing" className="hover:text-gray-700">Accueil</Link>
                    </div>
                </div>
            </footer>
        </div>
    );
}
